/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React from "react";
import PropTypes from "prop-types";
import FormGroup from "../../../common/core/FormGroup";
import Input from "../../../common/core/Input";
import Button from "../../../common/core/Button";
import Form from "../../../common/core/Form";
import Spinner from "../../../common/core/Spinner";
import ValidationConstants from "../../../common/utils/validationConstants";
import Alert from "../../../common/core/Alert";
import Label from "../../../common/core/Label";

const ChangePassword = (props) => {
  const {
    resetPasswordData,
    handleChange,
    handleSubmitResetPasswordDetails,
    toggleShowCurrentPassword,
    showCurrentPassword,
    toggleShowNewPassword,
    showNewPassword,
    toggleShowConfirmPassword,
    showConfirmPassword,
    changePasswordInProgress,
    error,
    handleSubmit,
    register,
    errors,
    message,
  } = props;

  const { IS_PASSWORD } = ValidationConstants;

  return (
    <div className="contents">
      <div className="login-outer position-absolute">
        <div className="login-panel">
          <div className="login-brand">
            <h4 className="mb-2">Change Password</h4>
            <p className="text-muted text-sm mb-4">
              Minimum 6 characters required.
              <br />
              At least 1 capital, 1 number & 1 special character.
            </p>
          </div>
          <Form onSubmit={handleSubmit(handleSubmitResetPasswordDetails)}>
            {!changePasswordInProgress && (
              <>
                {
                  message && (message.type === "success") && (
                    <Alert color={message.type}>
                      {message.message}
                    </Alert>
                  )
                }
              </>
            )}
            {errors.newPassword && (
              <>
                <Alert color="warning">
                  {errors.newPassword && errors.newPassword.message}
                </Alert>
              </>
            )}
            <fieldset disabled={changePasswordInProgress}>
              <FormGroup className="password">
                <Label for="currentPassword" className="me-sm-2 mb-2">Current Password:</Label>
                <Input
                  value={resetPasswordData.currentPassword}
                  onChange={(e) => handleChange(e)}
                  type={showCurrentPassword ? "text" : "password"}
                  name="currentPassword"
                  id=""
                  required
                />
                <Button
                  color={`transparent mt-3 ${showCurrentPassword ? "active mt-3" : ""}`}
                  onClick={toggleShowCurrentPassword}
                />
              </FormGroup>
              <p className="text-danger mb-3 fs-xs">
                {error}
              </p>
              <FormGroup className="password">
                <Label for="newPassword" className="me-sm-2 mb-2">New Password:</Label>
                <Input
                  value={resetPasswordData.newPassword}
                  onChange={(e) => handleChange(e)}
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  id=""
                  innerRef={register({
                    pattern: {
                      value: IS_PASSWORD,
                      message: "Invalid New Password ",
                    },
                  })}
                  required
                />
                <Button
                  color={`transparent mt-3  ${showNewPassword ? "active mt-3 " : ""}`}
                  onClick={toggleShowNewPassword}
                />
              </FormGroup>
              <FormGroup className="password">
                <Label for="confirmPassword" className="me-sm-2 mb-2">Re-Enter New Password:</Label>
                <Input
                  value={resetPasswordData.confirmPassword}
                  onChange={(e) => handleChange(e)}
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  id=""
                  required
                  innerRef={register({
                    validate: (value) => value === resetPasswordData.newPassword || "New password does not match",
                  })}
                />
                <Button
                  color={`transparent mt-3  ${showConfirmPassword ? "active mt-3 " : ""}`}
                  onClick={toggleShowConfirmPassword}
                />
              </FormGroup>
              <p className="text-danger mb-3 fs-xs">{errors.confirmPassword && errors.confirmPassword.message}</p>
              <FormGroup>
                <Button className="w-100" color="primary" block>
                  {changePasswordInProgress ? <Spinner size="sm" /> : "Submit"}
                </Button>
              </FormGroup>
            </fieldset>
          </Form>
        </div>
      </div>
    </div>
  );
};

ChangePassword.propTypes = {
  handleSubmitResetPasswordDetails: PropTypes.func.isRequired,
  resetPasswordData: PropTypes.objectOf(PropTypes.any).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  error: PropTypes.string.isRequired,
  errors: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleShowCurrentPassword: PropTypes.func.isRequired,
  showCurrentPassword: PropTypes.bool.isRequired,
  toggleShowNewPassword: PropTypes.func.isRequired,
  showNewPassword: PropTypes.bool.isRequired,
  toggleShowConfirmPassword: PropTypes.func.isRequired,
  showConfirmPassword: PropTypes.bool.isRequired,
  changePasswordInProgress: PropTypes.bool.isRequired,
  message: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default ChangePassword;
